import { HttpClient } from '@angular/common/http';
import { Directive, OnInit, OnDestroy, Input, EventEmitter, Output, SimpleChanges } from '@angular/core';
import { DataBindingDirective, GridComponent } from '@progress/kendo-angular-grid';
import { AggregateDescriptor, GroupDescriptor } from "@progress/kendo-data-query";
import { Subscription } from 'rxjs';
import { State, FilterDescriptor } from "@progress/kendo-data-query";
import { FormTemplateInstancesService } from '../services/form-template-instances.service';
import { FormField } from '../models/form-builder/form-field.model';
//import { FormTemplatePropertyInfo } from '../models/form-template-instances/form-template-property-info';
import { OrderFormsGridRowsData } from '../services/form-template-instances.service';

export class FormTemplateInstancesDirectiveData {
    public formTemplateId: number = 0;
    public columnDefinitions: FormField[] = null;
    public selectedGridId: number = -1;
    public formFieldId: number = 0;
    public aggregates: AggregateDescriptor[] = null;
    public formsGridOrdersJson: string;
    public canQueryData: boolean = true;
    public formInstanceId: number = 0;
}
/*
export class PredefinedFilter {
    public constructor(public fieldName: string, public filterValue: string, public filterOperator: string) {
    }
}
*/

@Directive({
    selector: '[formTemplateInstancesGridRemoteBinding]',
    exportAs: 'remoteBindingDirectiveRef',
    providers: [FormTemplateInstancesService],
    standalone: false
})
export class FormTemplateInstancesGridRemoteBindingDirective extends DataBindingDirective implements OnInit, OnDestroy {
    @Input() inputs: FormTemplateInstancesDirectiveData = null;

    @Output() notifyLoaded: EventEmitter<any> = new EventEmitter();

    // Other properties.
    private serviceSubscription: Subscription;

    // Constructor.
    public constructor(private http: HttpClient, grid: GridComponent, private formTemplateInstancesService: FormTemplateInstancesService) {
        super(grid);
    }

    // Life cycle metnhods.
    public ngOnInit(): void {
        this.serviceSubscription = this.formTemplateInstancesService.subscribe((formTemplateInstancesGridData) => {
            this.grid.loading = false;
            // Note:  the following line of code indicates that 'result.resultsAsPlainObjects' must contains the grid's data.
            //        Therefore, logic in FormTemplateInstancesService must set this property to the list of grid rows.
            this.grid.data = formTemplateInstancesGridData.resultsAsPlainObjects;
            // Begin test ordering code.
            /*
            if (formTemplateInstancesGridData.resultsAsPlainObjects.length == 2) {
                let removedObjectArray: object[] = formTemplateInstancesGridData.resultsAsPlainObjects.splice(1, 1);
                formTemplateInstancesGridData.resultsAsPlainObjects.splice(0, 0, removedObjectArray[0]);
            }
            */
            // End test order code.
            // If we have ordering data, use it.
            if ((this.inputs != null) && (this.inputs.formsGridOrdersJson != null) &&
                (this.inputs.formsGridOrdersJson.trim() != '') && (this.state?.sort?.length == 0)) {
                formTemplateInstancesGridData.resultsAsPlainObjects = FormTemplateInstancesGridRemoteBindingDirective.orderRowsByOrderData(formTemplateInstancesGridData.resultsAsPlainObjects, this.inputs.formsGridOrdersJson);
                this.grid.data = formTemplateInstancesGridData.resultsAsPlainObjects;
            }

            // Notify that data has been loaded.
            this.notifyDataChange();
            this.notifyLoaded.emit({ directive: this, count: this.grid.data?.length, gridData: formTemplateInstancesGridData });
        });

        super.ngOnInit();
        this.rebind();
    }

    /*
    public ngOnChanges(changes: SimpleChanges): void {
        console.log('ngOnChanges(): executing ...');

        if (changes.formsGridOrdersJson != null) {

        }
    }
    */

    public ngOnDestroy(): void {
        if (this.serviceSubscription) {
            this.serviceSubscription.unsubscribe();
        }

        super.ngOnDestroy();
    }

    public restoreSavedState(savedState: State): void {
        if (savedState.sort != null)
            this.state.sort = savedState.sort;
        if (savedState.filter != null)
            this.state.filter = savedState.filter;
    }

    // pharvey 9/28/2023 - I added this lastCallToRebind/throttle logic because for some reason
    // two calls were being made to rebind milliseconds apart when filtering/sorting.
    // Couldn't get to the bottom of the cause, so went with this workaround.
    private lastCallToRebind: number = 0;
    public rebind(): void {
        if (!this.inputs.canQueryData)
            return;

        let throttle = this.lastCallToRebind ? (Date.now() - this.lastCallToRebind) : 101;
        this.lastCallToRebind = Date.now();
        if (throttle > 100) {
            this.grid.loading = true;
            if ((this.inputs.formTemplateId != null) && (this.inputs.formTemplateId > 0)) {
                this.formTemplateInstancesService.read(this.inputs.formTemplateId, this.inputs.columnDefinitions, this.inputs.selectedGridId, this.inputs.formFieldId, this.state, null, this.inputs.formInstanceId);//, this.aggregates);
            }
        }
    }

    public enableCanQueryDataAndRebind(): void {
        //console.log('enableCanQueryDataAndRebind(): executing ...'); 
        this.inputs.canQueryData = true;
        this.rebind();
    }

    public canRebind(): boolean {
        let throttle = this.lastCallToRebind ? (Date.now() - this.lastCallToRebind) : 101;

        let canRebindResult: boolean = ((throttle > 100) && (!this.grid.loading));

        return canRebindResult;
    }

    /**
     * Removes the filters from the state
     */
    public ClearDirectiveState(): void {
        this.state.filter.filters = [];
    }

    /**
     * Ensures that grid filters are applied. This is especially useful
     * when there are multiple form grids on the page since the 2nd, etc
     * grids appear to only allow filters from here.
     * @param filters
     */
    public makeSureFiltersApplied(filters: FilterDescriptor[]): void {
        for (let index: number = 0; index < filters.length; index++) {
            let filter: FilterDescriptor = filters[index];

            if (this.state.filter != null) {
                if (this.state.filter.filters == null) 
                    this.state.filter.filters = [];

                if (!FormTemplateInstancesGridRemoteBindingDirective.filterExists(filter, this.state.filter.filters))
                    this.state.filter.filters.push(filter);
            }
        }
    }

    // A setter method (this bypasses the ordinary ngOnChanges() lifecycle that creates a timing complication.)
    public set CanQueryData(value: boolean) {
        this.inputs.canQueryData = value;
    }

    // Helper methods.
    private static filterExists(filter: FilterDescriptor, filters: object[]): boolean {
        let existingFilter: FilterDescriptor = null;

        for (let index: number = 0; index < filters.length; index++) {
            let searchFilter: FilterDescriptor = <FilterDescriptor>filters[index];
            if ((searchFilter.field == filter.field) && (searchFilter.value == filter.value) && (searchFilter.operator == filter.operator)) {
                existingFilter = searchFilter;
                break;
            }
        }

        return existingFilter != null;
    }

    private static orderRowsByOrderData(resultsAsPlainObjects: object[], formsGridOrdersJson: string): object[] {
        let mapOfOrderData: OrderFormsGridRowsData = <OrderFormsGridRowsData>JSON.parse(formsGridOrdersJson);

        let arrayOfRowsWithAssignedOrders: object[] = [];
        let arrayOfRowsWithoutAssignedOrders: object[] = [];
        for (let index: number = 0; index < resultsAsPlainObjects.length; index++) {
            let row: object = resultsAsPlainObjects[index];
            let formInstanceId = row['Id'];

            let assignedOrder: number = mapOfOrderData[formInstanceId];
            if (assignedOrder != null) {
                row['formsGridOrder'] = assignedOrder;
                arrayOfRowsWithAssignedOrders.push(row);
            } else {
                arrayOfRowsWithoutAssignedOrders.push(row);
            }
        }

        if (arrayOfRowsWithAssignedOrders.length > 1)
            arrayOfRowsWithAssignedOrders.sort(FormTemplateInstancesGridRemoteBindingDirective.sortByAsignedOrder);

        let resultArray: object[] = arrayOfRowsWithAssignedOrders.concat(arrayOfRowsWithoutAssignedOrders);
        return resultArray;
    }
    private static sortByAsignedOrder(row1: object, row2: object): number {
        let row1AssignedOrder: number = row1['formsGridOrder'];
        let row2AssignedOrder: number = row2['formsGridOrder'];

        let result: number = 0;
        if (row1AssignedOrder < row2AssignedOrder)
            result = -1;
        else if (row1AssignedOrder > row2AssignedOrder)
            result = 1;

        return result;
    }

    /*
    private addAnyPredefinedFilters(): void {
        if ((this.state != null) && (this.state.filter != null)) {
            if ((this.predefinedFilters != null) && (this.predefinedFilters.length > 0)) {
                if (this.state.filter.filters == null)
                    this.state.filter.filters = [];

                for (let index: number = 0; index < this.predefinedFilters.length; index++) {
                    //let predefinedFilter: PredefinedFilter = this.predefinedFilters[index];
                    let predefinedFilter: FormTemplatePropertyInfo = this.predefinedFilters[index];

                    let gridFilter = {
                        field: predefinedFilter.propertyName,//predefinedFilter.fieldName,
                        operator: predefinedFilter.defaultFilterOperator, //predefinedFilter.filterOperator,
                        value: predefinedFilter.optionalFilterValue//predefinedFilter.filterValue
                    }
                    this.state.filter.filters.push(gridFilter);
                } // for
            } // if
        } // if
    }
    */
}
